import { formatDate } from '../../lib/format'
import { Avatar } from '../../components/Avatar'
import { useSession } from '../../hooks/useSession'
import type { InfluenceRoute } from '../../types/accounts'
import type { ContactView } from '../../types/contacts'

// Who can open this door (REQ-078): the routes the API ranked, most senior person reached first, then by the
// tie's strength discounted by how long ago it was touched. Each is a path (the colleague whose mail holds
// the tie, the connector who introduces if any, the target), worded as the thing to DO — you reach them
// directly, or you ask a colleague or a connector for the introduction — with the evidence (touches, last
// contact, meetings) beside it. "Be the door opener, not the blocker. Enable introductions, bring people into
// the account" (Don, 2026-09-23). Click a person to open their memory.

export function WaysIn({ routes, contacts, ownerNames, onOpen }: {
  routes: InfluenceRoute[]; contacts: ContactView[]; ownerNames: Record<string, string>; onOpen: (personId: string) => void
}) {
  const { user } = useSession()
  const person = new Map(contacts.map((c) => [c.person.personId, c]))
  const owner = (id: string) => ownerNames[id] ?? id
  /** The ask, in one line: whose door it is and what to do about it. */
  const ask = (r: InfluenceRoute, viaName: string | null, targetName: string) => {
    const mine = user?.id === r.ownerId
    // the target's own assistant (PL-212): you ask for time, not for an introduction
    if (viaName && r.kind === 'assistant') return mine ? `Ask ${viaName} for time with ${targetName}` : `Ask ${owner(r.ownerId)} to go through ${viaName} for time with ${targetName}`
    if (viaName) return mine ? `Ask ${viaName} to introduce you to ${targetName}` : `Ask ${owner(r.ownerId)} to have ${viaName} introduce you to ${targetName}`
    return mine ? `You reach ${targetName} directly` : `Ask ${owner(r.ownerId)} to introduce you to ${targetName}`
  }
  return (
    <section className="ways-in card">
      <div className="card-head">
        <div>
          <h2>Who can open this door</h2>
          <p className="muted">Ranked by who they reach, then by the tie behind them and how recently it was touched. A warm tie is a door; a colleague or a warm connector opens one to the people above them.</p>
        </div>
      </div>
      {routes.length === 0 ? (
        <div className="ways-empty">
          <strong>Nobody at the firm holds a warm tie into this account yet.</strong>
          <p className="muted">A tie at 30% or better opens a door. Read more mail, or refresh relationships on Contacts; an introduction from a colleague or a connector appears here as soon as one tie is warm.</p>
        </div>
      ) : (
        <ol className="routes">
          {routes.map((r, i) => {
            const target = person.get(r.targetPersonId)
            const via = r.viaPersonId ? person.get(r.viaPersonId) : null
            const pct = Math.round(r.strength * 100)
            const band = pct >= 60 ? 'strong' : pct >= 30 ? 'warm' : 'faint'
            const line = ask(r, via?.person.name ?? null, target?.person.name ?? r.targetPersonId)
            const evidence = [
              r.touches > 0 ? `${r.touches} touch${r.touches === 1 ? '' : 'es'}` : null,
              r.lastContact ? `last ${formatDate(r.lastContact)}` : null,
              r.meetings > 0 ? `${r.meetings} meeting${r.meetings === 1 ? '' : 's'}` : null,
            ].filter(Boolean).join(' · ')
            return (
              <li key={`${r.ownerId}-${r.targetPersonId}-${r.viaPersonId ?? ''}`} className={`route ${via ? 'introduced' : 'direct'}`}>
                <span className="route-rank">{i + 1}</span>
                <div className="route-body">
                  <div className="route-path">
                    <span className="route-owner">{owner(r.ownerId)}</span>
                    <span className="route-arrow" aria-hidden="true">→</span>
                    {via && (
                      <>
                        <button className="route-person via" onClick={() => onOpen(via.person.personId)} title={via.person.title ?? ''}><Avatar name={via.person.name} photoUrl={via.person.photoUrl} persona={via.person.persona} role={via.person.role} size={22} className="flat" />{via.person.name}</button>
                        <span className="route-arrow dashed" aria-hidden="true">⇢</span>
                      </>
                    )}
                    <button className="route-person target" onClick={() => onOpen(r.targetPersonId)} title={target?.person.title ?? ''}>{target && <Avatar name={target.person.name} photoUrl={target.person.photoUrl} persona={target.person.persona} role={target.person.role} size={22} className="flat" />}{target?.person.name ?? r.targetPersonId}</button>
                    {target?.person.persona && <span className={`tag persona ${target.person.persona}`}>{target.person.persona}</span>}
                  </div>
                  {target?.person.title && <span className="muted small route-title">{target.person.title}</span>}
                  <p className="route-ask"><strong>{line}</strong></p>
                  <div className="route-meter">
                    <span className="strength-bar" title={`${pct}%`}><span className={`strength-fill ${band}`} style={{ width: `${Math.max(4, pct)}%` }} /></span>
                    <span className="mono small">{pct}%</span>
                    <span className="muted small">{r.kind === 'assistant' ? 'through their assistant' : via ? 'introduced' : 'direct'}</span>
                  </div>
                  <p className="route-why">{r.why}</p>
                  {evidence && <p className="route-evidence">{evidence}</p>}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
